import { createSelector } from "reselect";
import {
  ActivityStatus,
  BedStatusValue,
  BluetoothStatus,
  CalibrationStatus,
  DevicesStatusMap,
  DeviceStatusesNames,
  FirmwareStatus,
  OccupancyStatus,
  OnOffStatus,
  PostureStatus,
  PostureStatusValue,
  PowerStatus,
  SensorsStatus,
  UserNearbySettingsStatus,
  UserNearbyStatus,
  VITALS_LOW_RSSI,
  WhyNotActive,
  WifiStatus,
} from ".";
import { Device, DeviceType } from "../devices";
import {
  devicesSlicedBySelectedMonitorNode,
  devicesSlicedBySelectedNode,
  organizationHasWatch,
} from "../devices/selectors";
import { OrganizationNode } from "../organization";
import {
  currentUserBaseNode,
  pathToNode,
  userSelectedENDNodeId,
} from "../organization/selectors";
import { DeviceMinimizedMap, ResidentMap } from "../residents";
import { ResidentBedStatus, ResidentStatusMap } from "../residentStatuses";
import { IAppState } from "../types";

export const residentActiveBasedonDevicesCreator = () => {
  return createSelector(
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.activityStatuses,
    (_: IAppState, deviceIds: string[]): string[] => deviceIds,
    (devicesStatuses, deviceIds) => {
      let anyActive = false;
      deviceIds.forEach((deviceId) => {
        if (devicesStatuses[deviceId]) {
          if ((devicesStatuses[deviceId] as ActivityStatus)?.isActive)
            anyActive = true;
        }
      });
      return anyActive;
    }
  );
};

export const postrureStatusCreator = () => {
  return createSelector(
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.postureStatuses,
    (_: IAppState, deviceId: string): string => deviceId,
    (devicesStatuses, deviceId) => {
      return devicesStatuses[deviceId] as PostureStatus;
    }
  );
};

export const occupancyStatusCreator = () => {
  return createSelector(
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.occupancyStatuses,
    (_: IAppState, deviceId: string): string => deviceId,
    (devicesStatuses, deviceId) => {
      return devicesStatuses[deviceId] as OccupancyStatus;
    }
  );
};

export const firmwareStatusCreator = () => {
  return createSelector(
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.firmwareStatuses,
    (_: IAppState, deviceId: string): string => deviceId,
    (deviceStatuses, deviceId) => {
      return deviceStatuses[deviceId]
        ? (deviceStatuses[deviceId] as FirmwareStatus)
        : { id: DeviceStatusesNames.FIRMWARE };
    }
  );
};

export const bluetoothStatusCreator = () => {
  return createSelector(
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.bluetoothStatuses,
    (_: IAppState, deviceId: string): string => deviceId,
    (deviceStatuses, deviceId) => {
      return deviceStatuses[deviceId]
        ? (deviceStatuses[deviceId] as BluetoothStatus)
        : ({ id: DeviceStatusesNames.BLUETOOTH } as BluetoothStatus);
    }
  );
};

export const powerStatusCreator = () => {
  return createSelector(
    (state: IAppState): DevicesStatusMap => state.devicesStatuses.powerStatuses,
    (_: IAppState, deviceId: string): string => deviceId,
    (deviceStatuses, deviceId) => {
      return deviceStatuses[deviceId]
        ? (deviceStatuses[deviceId] as PowerStatus)
        : ({ id: DeviceStatusesNames.POWER } as PowerStatus);
    }
  );
};

export const wifiStatusCreator = () => {
  return createSelector(
    (state: IAppState): DevicesStatusMap => state.devicesStatuses.wifiStatuses,
    (_: IAppState, deviceId: string): string => deviceId,
    (deviceStatuses, deviceId) => {
      if (deviceStatuses[deviceId])
        return (
          (deviceStatuses[deviceId] as WifiStatus) ||
          ({ id: DeviceStatusesNames.WIFI } as WifiStatus)
        );
    }
  );
};

export const callibrationStatusCreator = () => {
  return createSelector(
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.callibrationStatuses,
    (_: IAppState, deviceId: string): string => deviceId,
    (deviceStatuses, deviceId) => {
      return deviceStatuses[deviceId] as CalibrationStatus;
    }
  );
};

export const onOffStatusCreator = () => {
  return createSelector(
    (state: IAppState): DevicesStatusMap => state.devicesStatuses.onOffStatuses,
    (_: IAppState, deviceId: string): string => deviceId,
    (deviceStatuses, deviceId) => {
      return deviceStatuses[deviceId] as OnOffStatus;
    }
  );
};

export const userNearbyStatusCreator = () => {
  return createSelector(
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.userNearbyStatuses,
    (_: IAppState, deviceId: string): string => deviceId,
    (deviceStatuses, deviceId) => {
      return deviceStatuses[deviceId] as UserNearbyStatus;
    }
  );
};

export const userNearbySettingsStatusCreator = () => {
  return createSelector(
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.userNearbySettingsStatuses,
    (_: IAppState, deviceId: string): string => deviceId,
    (deviceStatuses, deviceId) => {
      return deviceStatuses[deviceId] as UserNearbySettingsStatus;
    }
  );
};

export const activityStatusCreator = () => {
  return createSelector(
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.activityStatuses,
    (_: IAppState, deviceId: string): string => deviceId,
    (deviceStatuses, deviceId) => {
      return deviceStatuses[deviceId] as ActivityStatus;
    }
  );
};

export const sensorsStatusCreator = () => {
  return createSelector(
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.sensorsStatuses,
    (_: IAppState, deviceId: string): string => deviceId,
    (deviceStatuses, deviceId) => {
      return deviceStatuses[deviceId] as SensorsStatus;
    }
  );
};

export const temperatureCreator = () => {
  return createSelector(
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.sensorsStatuses,
    (_: IAppState, deviceIds: string[]): string[] => deviceIds,
    (deviceStatuses, deviceIds) => {
      let res;
      deviceIds.map((id: string) => {
        const sens = deviceStatuses[id] as SensorsStatus;
        if (sens?.temperature?.value) res = Math.round(sens.temperature.value);
        return null;
      });
      return res;
    }
  );
};

export const devicesStatusesSlicedBySelectedNode = createSelector(
  devicesSlicedBySelectedNode,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userSelectedENDNodeId,
  currentUserBaseNode,
  (state: IAppState): DevicesStatusMap => state.devicesStatuses.wifiStatuses,
  (state: IAppState): DevicesStatusMap => state.devicesStatuses.powerStatuses,
  (
    devices,
    organizationNodes,
    userSelectedNodeId,
    currentUserBaseNode,
    wifiStatuses,
    powerStatuses
  ) => {
    let devicesToGetStatuses = [];
    if (userSelectedNodeId === currentUserBaseNode)
      devicesToGetStatuses = devices;
    const isResidentUnderNode = (residentNodeId: number, maybeTop: number) => {
      const selectedNodePath = pathToNode(
        organizationNodes,
        residentNodeId,
        false
      );
      return selectedNodePath.some((el) => el.id === maybeTop);
    };
    devicesToGetStatuses = devices.filter((resident: Device) =>
      isResidentUnderNode(resident.nodeId, userSelectedNodeId)
    );
    const resultStatuses = {};
    devicesToGetStatuses.map((dev: Device) => {
      if (wifiStatuses[dev.id])
        resultStatuses[dev.id + DeviceStatusesNames.WIFI] =
          wifiStatuses[dev.id];
      if (powerStatuses[dev.id])
        resultStatuses[dev.id + DeviceStatusesNames.POWER] =
          powerStatuses[dev.id];
      // Object.keys(DeviceStatusesNames).map((el: DeviceStatusesNames) => {
      //     const value = DeviceStatusesNames[el];
      //     if (devicesStatuses[dev.id + value]) resultStatuses[dev.id + value] = devicesStatuses[dev.id + value];
      // });
      return null;
    });
    return resultStatuses;
  }
);

export const devicesStatusesSlicedByFilterNodeType = createSelector(
  devicesSlicedBySelectedMonitorNode,
  (state: IAppState): DevicesStatusMap => state.devicesStatuses.wifiStatuses,
  (state: IAppState): DevicesStatusMap => state.devicesStatuses.powerStatuses,
  (state: IAppState): DevicesStatusMap =>
    state.devicesStatuses.activityStatuses,
  (state: IAppState): DevicesStatusMap =>
    state.devicesStatuses.occupancyStatuses,
  (state: IAppState): DevicesStatusMap =>
    state.devicesStatuses.userNearbyStatuses,
  (
    devices,
    wifiStatuses,
    powerStatuses,
    activityStatuses,
    occupancyStatuses,
    userNearByStatuses
  ) => {
    const resultStatuses = {};
    devices.map((dev: Device) => {
      if (dev.type === DeviceType.vitals) {
        const act: ActivityStatus = activityStatuses[dev.id] as ActivityStatus;
        if (act?.isActive) {
          if (powerStatuses[dev.id])
            resultStatuses[dev.id + DeviceStatusesNames.POWER] =
              powerStatuses[dev.id];
          if (wifiStatuses[dev.id])
            resultStatuses[dev.id + DeviceStatusesNames.WIFI] =
              wifiStatuses[dev.id];
        } else {
          if (act?.status === WhyNotActive.NO_BATTERY) {
            if (powerStatuses[dev.id])
              resultStatuses[dev.id + DeviceStatusesNames.POWER] =
                powerStatuses[dev.id];
          } else if (
            wifiStatuses[dev.id] &&
            act?.status === WhyNotActive.NO_WIFI
          )
            resultStatuses[dev.id + DeviceStatusesNames.WIFI] = {
              id: DeviceStatusesNames.WIFI,
            };
        }

        resultStatuses[dev.id + DeviceStatusesNames.USER_NEARBY] =
          userNearByStatuses[dev.id];
        resultStatuses[dev.id + DeviceStatusesNames.OCCUPANCY] =
          occupancyStatuses[dev.id];
        resultStatuses[dev.id + DeviceStatusesNames.ACTIVITY] =
          activityStatuses[dev.id];
      } else {
        if (activityStatuses[dev.id])
          resultStatuses[dev.id + DeviceStatusesNames.ACTIVITY] =
            activityStatuses[dev.id];
        if (powerStatuses[dev.id])
          resultStatuses[dev.id + DeviceStatusesNames.POWER] =
            powerStatuses[dev.id];
        if (wifiStatuses[dev.id])
          resultStatuses[dev.id + DeviceStatusesNames.WIFI] = {
            id: DeviceStatusesNames.WIFI,
          };
      }
      return null;
    });
    return resultStatuses;
  }
);
export const doesAnyDeviceHasProblemCreator = () => {
  return createSelector(
    (state: IAppState): DevicesStatusMap => state.devicesStatuses.powerStatuses,
    (state: IAppState): DevicesStatusMap => state.devicesStatuses.wifiStatuses,
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.bluetoothStatuses,
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.activityStatuses,
    (_: IAppState, devicesMap: DeviceMinimizedMap): DeviceMinimizedMap =>
      devicesMap,
    (
      powerStatuses,
      wifiStatuses,
      bluetoothStatuses,
      activityStatuses,
      devicesMap
    ) => {
      let hasProblems = false;
      Object.keys(devicesMap).forEach((deviceId) => {
        const type = devicesMap[deviceId].type;
        const activityStatus: ActivityStatus = activityStatuses[
          deviceId
        ] as ActivityStatus;
        const powerStatus = powerStatuses[deviceId] as PowerStatus;
        if (type === DeviceType.vitals) {
          if (!activityStatus?.isActive) {
            hasProblems = true;
            return;
          }
          const powerStatus = powerStatuses[deviceId] as PowerStatus;
          if (!powerStatus?.connected) {
            hasProblems = true;
            return;
          }
          const wifiStatus = wifiStatuses[deviceId] as WifiStatus;
          if (!wifiStatus || Object.keys(wifiStatus).length === 1)
            hasProblems = true;
          if (wifiStatus?.rssiDbm <= VITALS_LOW_RSSI) hasProblems = true;
        } else if (type === DeviceType.biopatch) {
          if (powerStatus?.batteryLevel < 30) {
            hasProblems = true;
            return;
          }
          const bluetoothStatus = bluetoothStatuses[
            deviceId
          ] as BluetoothStatus;
          if (!bluetoothStatus || Object.keys(bluetoothStatus).length === 1)
            hasProblems = true;
          if (
            //problems with rssi for biopatch - offline with not empty battery
            (activityStatuses[deviceId] as ActivityStatus)?.isActive ===
              false &&
            powerStatus?.batteryLevel > 0
          )
            hasProblems = true;
          //not attached
        } else if (type === DeviceType.watch) {
          if (!activityStatus?.isActive) {
            hasProblems = true;
            return;
          }
          if (powerStatus?.batteryLevel < 30) {
            hasProblems = true;
            return;
          }
        }
      });
      return hasProblems;
    }
  );
};

export const anyResidentDeviceInBedOrAttchedStatusCreator = () => {
  return createSelector(
    (state: IAppState): ResidentMap => state.residents.residents,
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.activityStatuses,
    (_: IAppState, residentId: string): string => residentId,
    (residents, activityStatuses, residentId) => {
      const resident = residents[residentId];
      const biopatchOrAnyId = Object.keys(resident.devicesMinimized).find(
        (el) => resident.devicesMinimized[el].type === DeviceType.biopatch
      );
      const vitalsIds = Object.keys(resident.devicesMinimized).filter(
        (el) => resident.devicesMinimized[el].type === DeviceType.vitals
      );
      const anyVitals = vitalsIds.find((id) => {
        return (activityStatuses[id] as ActivityStatus)?.isActive === true;
      });
      //
      const anyBio = biopatchOrAnyId
        ? (activityStatuses[biopatchOrAnyId] as ActivityStatus)?.isActive ===
          true
        : false;
      return anyVitals !== undefined || anyBio;
    }
  );
};

export const isBiopatchDetecting = () => {
  return createSelector(
    (state: IAppState): ResidentMap => state.residents.residents,
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.activityStatuses,
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.postureStatuses,
    (_: IAppState, residentId: string): string => residentId,
    (residents, activityStatuses, postureStatuses, residentId) => {
      const resident = residents[residentId];
      const biopatchOrAnyId = Object.keys(resident.devicesMinimized).find(
        (el) => resident.devicesMinimized[el].type === DeviceType.biopatch
      );

      const anyBio = biopatchOrAnyId
        ? (activityStatuses[biopatchOrAnyId] as ActivityStatus)?.isActive ===
            true &&
          (postureStatuses[biopatchOrAnyId] as PostureStatus).value !==
            PostureStatusValue.UNATTACHED
        : false;

      return anyBio;
    }
  );
};

export const isVitalsDetecting = () => {
  return createSelector(
    (state: IAppState): ResidentMap => state.residents.residents,
    (state: IAppState): ResidentStatusMap => state.residentStatuses.bedStatuses,
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.occupancyStatuses,
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.activityStatuses,
    (state: IAppState): DevicesStatusMap => state.devicesStatuses.onOffStatuses,
    (_: IAppState, residentId: string): string => residentId,
    (
      residents,
      bedStatuses,
      occupancyStatuses,
      activityStatuses,
      onOffStatuses,
      residentId
    ) => {
      const resident = residents[residentId];

      const vitalsIds = Object.keys(resident.devicesMinimized).filter(
        (el) => resident.devicesMinimized[el].type === DeviceType.vitals
      );
      const statusFromResident = bedStatuses[residentId] as ResidentBedStatus;

      const anyVitals = vitalsIds.findIndex((id) => {
        const occup = occupancyStatuses[id] as OccupancyStatus;
        const status = occup
          ? { ...statusFromResident, value: occup.value }
          : statusFromResident;
        const onOff = onOffStatuses[id] as OnOffStatus;
        return (
          status?.value === BedStatusValue.IN_BED &&
          (activityStatuses[id] as ActivityStatus)?.isActive === true &&
          onOff.isOn
        );
      });

      return anyVitals !== -1 ? true : false;
    }
  );
};

export const isWatchDetecting = () => {
  return createSelector(
    (state: IAppState): ResidentMap => state.residents.residents,
    (state: IAppState): DevicesStatusMap =>
      state.devicesStatuses.activityStatuses,
    organizationHasWatch,
    (_: IAppState, residentId: string): string => residentId,
    (residents, activityStatuses, organizationHasWatch, residentId) => {
      const resident = residents[residentId];
      if (!organizationHasWatch) return false;
      const watchIds = Object.keys(resident.devicesMinimized).find(
        (el) => resident.devicesMinimized[el].type === DeviceType.watch
      );
      const anyBio = watchIds
        ? (activityStatuses[watchIds] as ActivityStatus)?.isActive === true
        : false;

      return anyBio;
    }
  );
};
